import React, { useState, useEffect } from 'react';
import { Modal, Input, Tooltip, message } from 'antd';
import { PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../axios/axiosInstance";
import {
  ActionButton,
  DataTable,
  PageCard,
  PageShell,
  PageToolbar,
  RowIconButton,
} from "../components/ui";

const Partners = () => {
  const { t } = useTranslation();
  const [partners, setPartners] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [newPartnerName, setNewPartnerName] = useState('');
  const [newPartnerPhone, setNewPartnerPhone] = useState('');

  const fetchPartners = async () => {
    try {
      const response = await axiosInstance.get('/partners');
      setPartners(response.data.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const openModal = (mode, partner = null) => {
    setModalMode(mode);
    if (mode === 'edit' && partner) {
      setSelectedPartner(partner);
      setNewPartnerName(partner.name);
      setNewPartnerPhone(partner.phone);
    } else {
      setNewPartnerName('');
      setNewPartnerPhone('');
    }
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedPartner(null);
  };

  const deletePartner = async (partnerId) => {
    try {
      await axiosInstance.delete(`/partners/${partnerId}`);
      setPartners(partners.filter((partner) => partner.id !== partnerId));
      message.success(t('partners.deleted'));
    } catch (error) {
      console.error('Error deleting partner:', error);
      message.error(t('partners.deleteError'));
    }
  };

  const addOrUpdatePartner = async () => {
    try {
      if (modalMode === 'add') {
        const response = await axiosInstance.post('/partners', {
          name: newPartnerName,
          phone: newPartnerPhone,
        });
        setPartners([...partners, response.data]);
        message.success(t('partners.created'));
      } else if (modalMode === 'edit' && selectedPartner) {
        await axiosInstance.put(`/partners/${selectedPartner.id}`, {
          name: newPartnerName,
          phone: newPartnerPhone,
        });
        setPartners(partners.map((partner) =>
            partner.id === selectedPartner.id
                ? { ...partner, name: newPartnerName, phone: newPartnerPhone }
                : partner
        ));
        message.success(t('partners.updated'));
      }
      closeModal();
      fetchPartners();
    } catch (error) {
      console.error('Error adding/updating partner:', error);
      message.error(t('partners.saveError'));
    }
  };

  const columns = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name) => <span className="text-sm font-medium text-[#1D2C7B]">{name}</span>,
    },
    {
      title: t('partners.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 190,
      render: (phone) => (
          <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{phone}</span>
      ),
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: 88,
      fixed: 'right',
      render: (text, record) => (
          <div className="flex items-center gap-0.5">
            <Tooltip title={t('common.edit')}>
              <RowIconButton onClick={() => openModal('edit', record)}>
                <PencilSquareIcon className="w-[18px] h-[18px]" />
              </RowIconButton>
            </Tooltip>
            <Tooltip title={t('partners.delete')}>
              <RowIconButton onClick={() => deletePartner(record.id)}>
                <TrashIcon className="w-[18px] h-[18px]" />
              </RowIconButton>
            </Tooltip>
          </div>
      ),
    },
  ];

  const renderMobileCard = (record) => (
      <div>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-[#1D2C7B] truncate">{record.name}</span>
            <span className="block text-xs text-slate-500 mt-0.5 tabular-nums">{t('common.id')}: {record.id}</span>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <RowIconButton onClick={() => openModal('edit', record)} aria-label={t('common.edit')}>
              <PencilSquareIcon className="w-[18px] h-[18px]" />
            </RowIconButton>
            <RowIconButton onClick={() => deletePartner(record.id)} aria-label={t('partners.delete')}>
              <TrashIcon className="w-[18px] h-[18px]" />
            </RowIconButton>
          </div>
        </div>

        {record.phone && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <a href={`tel:+${record.phone}`} className="text-xs text-[#1D2C7B] tabular-nums">
                {record.phone}
              </a>
            </div>
        )}
      </div>
  );

  return (
      <PageShell>
        <PageCard>
          <PageToolbar
              title={t('partners.title')}
              count={partners.length}
              actions={
                <ActionButton icon={<PlusIcon className="w-4 h-4" />} onClick={() => openModal('add')}>
                  {t('partners.add')}
                </ActionButton>
              }
          />

          <DataTable
              columns={columns}
              dataSource={partners}
              mobileCard={renderMobileCard}
              scrollX={700}
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                position: ['bottomCenter'],
                className: 'px-4 pb-3',
              }}
          />
        </PageCard>

        <Modal
            centered
            width="100%"
            style={{ maxWidth: 520 }}
            title={modalMode === 'add' ? t('partners.create') : t('partners.edit')}
            visible={isOpen}
            onCancel={closeModal}
            onOk={addOrUpdatePartner}
            okText={modalMode === 'add' ? t('common.add') : t('common.update')}
            cancelText={t('common.cancel')}
        >
          <div className="grid grid-cols-1 gap-4">
            <Input
                placeholder={t('common.name')}
                value={newPartnerName}
                onChange={(e) => setNewPartnerName(e.target.value)}
            />
            <Input
                placeholder={t('partners.phone')}
                value={newPartnerPhone}
                onChange={(e) => setNewPartnerPhone(e.target.value)}
            />
          </div>
        </Modal>
      </PageShell>
  );
};

export default Partners;
